import { ListContextProvider, useListContext, useListController } from "ra-core"
import { FC } from 'react';
import { Grid, Box, Card, Divider, Typography, Link, Button } from "@mui/material";
import { Link as RouteLink } from "react-router-dom";
import { OdResourcePageProps } from "~/types/resource";
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import { Content } from "~/components/content/Content"
import { Datagrid } from "~/components/list/datagrid";
import Scrollbar from "~/components/Scrollbar";
import { FileCataSearchContextProvider } from "~/components/securefiles/FileCataSearchContextProvider";
import { FileCataAmbientDataContextProvider, useFileCataAmbientDataContext } from "~/components/securefiles/FileCataAmbientDataProvider";
import FileCatalogSearchRegion from "~/components/securefiles/FileCatalogSearchRegion";
import RowExpandFileCataEdit from "~/components/securefiles/RowExpandFileCataEdit";
import { FileResponseDto } from "~/gen/api/securefile";
import { RecPatch } from "~/dataProvider/tools";
import { FunctionField, TextField } from "~/components/field";
import ClassificationLabel from "~/components/governance/labels/ClassificationLabel";
import { genUrlWithHashid } from "~/utils/hashid";
import { usePushBreadcrumb } from "~/utils/usePushBreadcrumb";


const pageMark = { label: 'File Catalog' }

const FileCatalog: FC<OdResourcePageProps> = (props) => {
  const listCtrl = useListController({ basePath: '' })
  usePushBreadcrumb(pageMark, { toMatch: '/secure-files/'})

  return (
    <ListContextProvider value={listCtrl}>
      <FileCataSearchContextProvider>
        <FileCataAmbientDataContextProvider>
          <FileCatalogBody {...props} />
        </FileCataAmbientDataContextProvider>
      </FileCataSearchContextProvider>
    </ListContextProvider>
  )
}

export const FileCatalogBody: FC<OdResourcePageProps> = ({
  options,
}) => {
  const listCtx = useListContext()
  const { classifications, tags } = useFileCataAmbientDataContext()

  return (
    <Content pageName="File Catalog" resourceName="File Catalog" breadcrumbs={options?.breadcrumbs}
      secondaryActions={
        <>
          <Button size="small"
            variant="text"
            component={RouteLink} 
            to="/secure-files/classification-rules"
          >
            View Classification Rules
          </Button>
          <Button size="small"
            variant="text"
            component={RouteLink} 
            to="/secure-files/data-protection-rules"
          >
            View Data Protection Rules
          </Button>
        </>
      }
    >
      <Grid
        container
        spacing={4}
        sx={{ mt: 2 }}
      >
        <Grid item xs={12}>
          <Card>
            <FileCatalogSearchRegion />
            <Divider />
            <Scrollbar>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid
                  hasBulkActions={false}
                  expand={<RowExpandFileCataEdit />}
                  sx={{
                    '& th' : { py: 1, textTransform: 'uppercase' },
                    '& td' : { py: 2 },
                    '& span.MuiTypography-root.filename' : { maxWidth:400, wordBreak:'break-word' },
                  }}
                >
                  <FunctionField source="fileName" label={<Box component="span" sx={{ maxWidth: 100 }}>Name</Box>} sortable={false} 
                    render={(d?:FileResponseDto&RecPatch) =>
                      <Typography component="span" variant="body2" className="filename">
                        <Link underline="none"
                          component={RouteLink}
                          to={genUrlWithHashid('/secure-files/$0/show', d?.fileId ?? 0, d?.fileName)}
                        >
                          {d?.fileName}
                        </Link>
                      </Typography>
                    }
                  />
                  <FunctionField source="secondaryClassificationId" label="Classification" sortable={false} 
                    render={(d?:FileResponseDto&RecPatch) => {
                      const clss = classifications.find(c => c.secondaryClassificationId === d?.secondaryClassificationId)
                      if (!clss) { return null }
                      return <ClassificationLabel variant={clss.primaryClassification!} text={clss.name!} />;
                    }}
                  />
                  <FunctionField source="parentFileName" label="Parent File" sortable={false} 
                    render={(d?:FileResponseDto&RecPatch) => 
                      Boolean(d?.parentFileId) &&
                      <Typography component="span" variant="body2" className="filename">
                        <Link underline="none" color={d?.parentFileDeleted ? 'error' : undefined}
                          component={RouteLink}
                          to={genUrlWithHashid('/secure-files/$0/show', d?.parentFileId ?? 0, d?.parentFileName)}
                        >
                          {d?.parentFileName}
                        </Link>
                      </Typography>
                    }
                  />
                  <FunctionField source="tags" label="Tags" sortable={false} 
                    render={(d?:FileResponseDto&RecPatch) => {
                      const filetags = (d?.tags ?? []).map(tid => tags.find(t => t.tagId === tid)).filter(x => !!x)
                      return (
                        <Box sx={{
                          maxWidth: 180,
                          '& p': { fontStyle: 'italic' }
                        }}>
                          {filetags.map(t =>
                            <Typography key={t?.tagId} variant="caption" noWrap component="p">{t?.name}</Typography>
                          )}
                        </Box>
                      )
                    }}
                  />
                  <TextField source="encryptedByUser" label="Encrypted By" sortable={false} />
                </Datagrid>
              </Box>
            </Scrollbar>
            {//@ts-ignore
              <DefaultPagination {...listCtx} />}
          </Card>
        </Grid>
      </Grid>

    </Content>
  )
}


export default FileCatalog;